<script>
import linkTypes from '@shared/assets/data/social_links.json';
import TrainingsList from '@school/components/TrainingsList.vue';
import CustomerTrainingsList from '@school/components/CustomerTrainingsList.vue';
import BundlesList from '@school/components/BundlesList.vue';
import MembershipsList from '@school/components/MembershipsList.vue';
import RegisterForm from '@school/components/RegisterForm.vue';
import APIMemberships from '@school/services/API/Memberships';
import ConfirmationPrompt from '@school/components/ConfirmationPrompt.vue';
import ConfirmActionModal from '@school/components/ConfirmActionModal.vue';
import buildMembershipPriceBuyable from '@school/utils/membership';

export default {
  name: 'Dashboard',
  components: {
    TrainingsList,
    CustomerTrainingsList,
    BundlesList,
    MembershipsList,
  },
  static: { linkTypes },
  data: () => ({
    isFetching: false,
  }),
  computed: {
    store() {
      return this.$store.state.store.data;
    },
    authCustomer() {
      return this.$store.getters['auth/customer'];
    },
    customerTrainingsAsStudentAndBlocked() {
      return this.$store.getters['auth/customerTrainingsAsStudentAndBlocked'];
    },
    customerTrainingsAsAbandoned() {
      return this.$store.getters['auth/customerTrainingsAsAbandoned'];
    },
    customerMemberships() {
      return this.$store.getters['auth/customerMemberships'];
    },
    recentFailedMemberships() {
      return this.$store.getters['auth/recentFailedMemberships'];
    },
    storeTrainings() {
      return this.store?.trainings || [];
    },
    storeBundles() {
      return this.store?.bundles || [];
    },
    storeMemberships() {
      return this.store?.memberships || [];
    },
    allOffersTabIndex() {
      return 2;
    },
    tabLength() {
      return 3;
    },
    allOffersCount() {
      return this.storeMemberships.length + this.storeBundles.length + this.storeTrainings.length;
    },
    confirmationText() {
      return this.$t('subscription_confirmation_text');
    },
  },
  created() {
    this.isFetching = true;
    Promise.all([
      this.$store.dispatch('auth/fetch'),
      this.$store.dispatch('auth/fetchMemberships'),
    ])
      .finally(() => {
        this.isFetching = false;
      });
  },
  methods: {
    cancelSubscription(subscription) {
      const { confirmationText } = this;
      const cancelText = this.$t('subscription_cancel_cancel_button');

      let modalInstance;

      const closeModal = () => {
        if (modalInstance) {
          modalInstance.close();
        }
      };

      const props = {
        title: this.$t('subscription_cancel_confirm_title'),
        message: this.$t('subscription_cancel_confirm_message', {
          name: subscription.membership.name,
          confirmation: confirmationText,
        }),
        confirmationText,
        confirmText: this.$t('subscription_cancel_confirm_button'),
        cancelText,
        type: 'is-danger',
      };

      const events = {
        cancel: closeModal,
        close: closeModal,
        confirm: ({ setProcessing }) => {
          setProcessing(true);
          APIMemberships.cancelMembership(subscription.uuid)
            .then(() => {
              this.$buefy.toast.open({
                message: this.$t('subscription_cancel_success'),
                type: 'is-success',
              });

              // Refresh subscription data
              this.$store.dispatch('auth/fetchMemberships');
            })
            .catch((error) => {
              this.$buefy.toast.open({
                message: this.$t('subscription_cancel_error'),
                type: 'is-danger',
              });
              console.error('Error canceling subscription:', error);
            })
            .finally(() => {
              setProcessing(false);
              closeModal();
            });
        },
      };

      modalInstance = this.$buefy.modal.open({
        parent: this,
        component: ConfirmationPrompt,
        hasModalCard: true,
        trapFocus: true,
        props,
        events,
      });
    },
    resumeSubscription(subscription) {
      let modalInstance;

      const closeModal = () => {
        if (modalInstance) {
          modalInstance.close();
        }
      };

      const props = {
        title: this.$t('subscription_resume_confirm_title'),
        message: this.$t('subscription_resume_confirm_message', { name: subscription.membership.name }),
        confirmText: this.$t('subscription_resume_confirm_button'),
        cancelText: this.$t('subscription_resume_cancel_button'),
        type: 'is-success',
      };

      const events = {
        cancel: closeModal,
        close: closeModal,
        confirm: ({ setProcessing }) => {
          setProcessing(true);
          APIMemberships.resumeMembership(subscription.uuid)
            .then(() => {
              this.$buefy.toast.open({
                message: this.$t('subscription_resume_success'),
                type: 'is-success',
              });

              // Refresh subscription data
              this.$store.dispatch('auth/fetchMemberships');
            })
            .catch((error) => {
              this.$buefy.toast.open({
                message: this.$t('subscription_resume_error'),
                type: 'is-danger',
              });
              console.error('Error resuming subscription:', error);
            })
            .finally(() => {
              setProcessing(false);
              closeModal();
            });
        },
      };

      modalInstance = this.$buefy.modal.open({
        parent: this,
        component: ConfirmActionModal,
        hasModalCard: true,
        trapFocus: true,
        props,
        events,
      });
    },
    subscribe(subscription) {
      const membership = subscription?.membership;
      const priceUuid = subscription?.price?.uuid;
      const buyable = buildMembershipPriceBuyable(membership, priceUuid);

      if (!buyable) {
        this.$buefy.toast.open({
          message: this.$t('subscription_no_active_prices'),
          type: 'is-danger',
        });
        return;
      }

      const analyticsValue = buyable.discount_price && buyable.discount_price > 0
        ? buyable.discount_price
        : buyable.amount;

      let modalInstance;
      const closeModal = () => {
        if (modalInstance) {
          modalInstance.close();
        }
      };

      const handleEnroll = () => {
        this.$store.dispatch('auth/fetchMemberships')
          .finally(() => {
            this.$buefy.toast.open({
              type: 'is-success',
              message: this.$t('subscription_enroll_success'),
            });
          });
        closeModal();
      };

      modalInstance = this.$buefy.modal.open({
        parent: this,
        component: RegisterForm,
        props: {
          buyable,
        },
        hasModalCard: false,
        canCancel: ['escape', 'outside'],
        width: '520px',
        events: {
          enroll: handleEnroll,
          complete: closeModal,
          close: closeModal,
        },
      });

      if (this.$gtag) {
        this.$gtag.addToCart({
          items: [{
            id: membership.uuid,
            name: membership.name,
            quantity: 1,
            value: analyticsValue,
          }],
        });
      }

      if (this.$fbq) {
        this.$fbq.addToCart({
          content_ids: [membership.uuid],
          content_name: membership.name,
          value: analyticsValue,
        });
      }
    },
  },
  i18n: {
    messages: {
      fr: {
        dashboard_welcome: 'Bienvenue',
        dashboard_description: 'Retrouvez et suivez l\'avancement de toutes vos formations ici',
        dashboard_tab_my_trainings: 'Mes formations ({count})',
        dashboard_tab_pending: 'En attente ({count})',
        dashboard_tab_all_offers: 'Toutes les offres ({count})',
        dashboard_all_offers_memberships_title: 'Abonnements',
        dashboard_all_offers_bundles_title: 'Packs',
        dashboard_all_offers_trainings_title: 'Formations',
        dashboard_tab_subscriptions: 'Mes abonnements ({count})',
        subscription_status_active: 'Actif',
        subscription_status_pending: 'En attente',
        subscription_status_canceled: 'Annulé',
        subscription_status_cancel_at_period_end: 'Annulation programmée',
        subscription_cancel: 'Annuler l\'abonnement',
        subscription_resubscribe: 'Se réabonner',
        subscription_resume: 'Reprendre l\'abonnement',
        subscription_resume_confirm_title: 'Reprendre l\'abonnement',
        subscription_resume_confirm_message: 'Voulez-vous reprendre votre abonnement "{name}" ? Il continuera jusqu\'à la prochaine échéance.',
        subscription_resume_confirm_button: 'Reprendre',
        subscription_resume_success: 'Abonnement repris avec succès',
        subscription_resume_error: 'Erreur lors de la reprise de l\'abonnement',
        subscription_resume_cancel_button: 'Annuler',
        subscription_cancel_confirm_title: 'Confirmer l\'annulation',
        subscription_cancel_confirm_message: 'Êtes-vous sûr de vouloir annuler votre abonnement "{name}" ?<br><br><strong>Pour confirmer, tapez "{confirmation}" dans le champ ci-dessous :</strong>',
        subscription_cancel_confirm_button: 'Annuler l\'abonnement',
        subscription_cancel_success: 'Abonnement annulé avec succès',
        subscription_cancel_error: 'Erreur lors de l\'annulation de l\'abonnement',
        subscription_cancel_cancel_button: 'Annuler',
        subscription_confirmation_text: 'je confirme',
        subscription_no_active_prices: 'Aucun prix actif disponible pour cet abonnement',
        subscription_enroll_success: 'Abonnement effectué avec succès !',
      },
      en: {
        dashboard_welcome: 'Welcome',
        dashboard_description: 'Get new trainings and follow up your progress here',
        dashboard_tab_my_trainings: 'My trainings ({count})',
        dashboard_tab_pending: 'Pending ({count})',
        dashboard_tab_all_offers: 'All offers ({count})',
        dashboard_all_offers_memberships_title: 'Subscriptions',
        dashboard_all_offers_bundles_title: 'Bundles',
        dashboard_all_offers_trainings_title: 'Trainings',
        dashboard_tab_subscriptions: 'My subscriptions ({count})',
        subscription_status_active: 'Active',
        subscription_status_pending: 'Pending',
        subscription_status_canceled: 'Canceled',
        subscription_status_cancel_at_period_end: 'Scheduled for cancellation',
        subscription_cancel: 'Cancel Subscription',
        subscription_resubscribe: 'Resubscribe',
        subscription_resume: 'Resume Subscription',
        subscription_resume_confirm_title: 'Resume Subscription',
        subscription_resume_confirm_message: 'Do you want to resume your "{name}" subscription? It will continue until the next billing period.',
        subscription_resume_confirm_button: 'Resume',
        subscription_resume_success: 'Subscription resumed successfully',
        subscription_resume_error: 'Error resuming subscription',
        subscription_resume_cancel_button: 'Cancel',
        subscription_cancel_confirm_title: 'Confirm Cancellation',
        subscription_cancel_confirm_message: 'Are you sure you want to cancel your "{name}" subscription?<br><br><strong>To confirm, type "{confirmation}" in the field below:</strong>',
        subscription_cancel_confirm_button: 'Cancel Subscription',
        subscription_cancel_success: 'Subscription canceled successfully',
        subscription_cancel_error: 'Error canceling subscription',
        subscription_cancel_cancel_button: 'Cancel',
        subscription_confirmation_text: 'i confirm',
        subscription_no_active_prices: 'No active prices available for this subscription',
        subscription_enroll_success: 'Subscription completed successfully!',
      },
    },
  },
};
</script>

<template>
  <div class="dcomp page px-5 py-10">
    <h1 class="title is-custom has-text-centered is-size-6-touch">
      {{ $t('dashboard_welcome') }} <strong class="tdecoration-underline">{{ authCustomer.firstname }}</strong>
    </h1>
    <div class="content has-text-centered">
      <p>{{ $t('dashboard_description') }}</p>
    </div>

    <RenderlessToggle #default="{ isActive, setActive }" :active="0" :length="tabLength">
      <div>
        <nav class="tabs">
          <ul>
            <li :class="{ 'is-active': isActive === 0 }">
              <a @click="setActive(0)">
                {{ $t('dashboard_tab_my_trainings', { count: customerTrainingsAsStudentAndBlocked.length }) }}
              </a>
            </li>
            <li :class="{ 'is-active': isActive === 1 }">
              <a @click="setActive(1)">
                {{ $t('dashboard_tab_pending', { count: customerTrainingsAsAbandoned.length }) }}
              </a>
            </li>
            <li :class="{ 'is-active': isActive === allOffersTabIndex }">
              <a @click="setActive(allOffersTabIndex)">
                {{ $t('dashboard_tab_all_offers', { count: allOffersCount }) }}
              </a>
            </li>
          </ul>
        </nav>
        <CustomerTrainingsList v-if="isActive === 0" :trainings="customerTrainingsAsStudentAndBlocked"
          :customer="authCustomer" />
        <CustomerTrainingsList v-else-if="isActive === 1" :trainings="customerTrainingsAsAbandoned"
          :customer="authCustomer" />
        <div v-else-if="isActive === allOffersTabIndex" class="all-offers">
          <section v-if="storeMemberships.length">
            <h3 class="title is-5 is-size-6-touch has-text-weight-semibold">
              {{ $t('dashboard_all_offers_memberships_title') }}
            </h3>
            <MembershipsList :memberships="storeMemberships" :total="storeMemberships.length" :has-title="false"
              :has-price="false" :is-small="true" />
          </section>

          <section v-if="storeBundles.length" class="mt-5">
            <h3 class="title is-5 is-size-6-touch has-text-weight-semibold">
              {{ $t('dashboard_all_offers_bundles_title') }}
            </h3>
            <BundlesList :bundles="storeBundles" :has-title="false" :has-price="false" :total="storeBundles.length"
              is-small />
          </section>

          <section v-if="storeTrainings.length" class="mt-5">
            <h3 class="title is-5 is-size-6-touch has-text-weight-semibold">
              {{ $t('dashboard_all_offers_trainings_title') }}
            </h3>
            <TrainingsList :trainings="storeTrainings" :customerTrainings="customerTrainingsAsStudentAndBlocked"
              :total="storeTrainings.length" :has-title="false" :has-price="false" is-small />
          </section>
        </div>
      </div>
    </RenderlessToggle>

  </div>
</template>
